import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Archive, CheckCircle2, Gift, LogOut, Sparkles, Trash2, UserCog, Users } from 'lucide-react';
import type { ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { useAuthStore } from '@/features/auth/authStore';
import { cn } from '@/lib/cn';
import { useI18n, type TranslationKey } from '@/i18n/i18n';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { api } from '@/lib/api';

type NavIcon = ComponentType<{ className?: string }>;

interface FriendProfile {
  slug: string;
  displayName: string;
  avatarUrl: string | null;
}

const links = [
  { to: '/', label: 'header.active', icon: Sparkles, end: true },
  { to: '/completed', label: 'header.fulfilled', icon: CheckCircle2 },
  { to: '/archive', label: 'header.archive', icon: Archive },
  { to: '/trash', label: 'header.trash', icon: Trash2 },
] satisfies Array<{
  to: string;
  label: TranslationKey;
  icon: NavIcon;
  end?: boolean;
}>;

export function Header() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const { t } = useI18n();
  const friend = useQuery({
    queryKey: ['profile-friend'],
    queryFn: () => api.get<FriendProfile | null>('/api/profile/friend'),
    staleTime: 10 * 60 * 1000,
    enabled: user != null,
  });

  if (!user) return null;

  return (
    <header className="app-header">
      <div className="app-header__inner">
        <Link to="/" className="app-header__brand">
          <span className="app-header__brand-mark">
            <Gift className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <div className="app-header__brand-title">{t('app.name')}</div>
            <div className="app-header__brand-subtitle">
              {t('header.signedInAs', { name: user.displayName })}
            </div>
          </div>
        </Link>

        <div className="app-header__actions">
          <LanguageSwitcher />
          <Button
            variant="ghost"
            size="sm"
            className="app-header__action"
            onClick={() => navigate('/settings')}
            title={t('header.profileSettings')}
            aria-label={t('header.profileSettings')}
          >
            <UserCog className="h-4 w-4" />
            <span className="app-header__action-text">{t('header.profile')}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="app-header__action"
            onClick={() => {
              void logout().then(() => navigate('/login'));
            }}
            title={t('header.logout')}
            aria-label={t('header.logout')}
          >
            <LogOut className="h-4 w-4" />
            <span className="app-header__action-text">{t('header.logout')}</span>
          </Button>
        </div>
      </div>

      <nav className="app-header__nav">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            className={({ isActive }) =>
              cn(
                'app-header__nav-link',
                isActive && 'app-header__nav-link--active',
              )
            }
          >
            <l.icon className="h-4 w-4" />
            {t(l.label)}
          </NavLink>
        ))}
        {friend.data && (
          <NavLink
            to={`/u/${friend.data.slug}`}
            className={({ isActive }) =>
              cn(
                'app-header__nav-link',
                isActive && 'app-header__nav-link--active',
              )
            }
          >
            <Users className="h-4 w-4" />
            {t('header.friendWishes', { name: friend.data.displayName })}
          </NavLink>
        )}
      </nav>
    </header>
  );
}
